/*
  <mf-slider> — replica of the Webflow slider used on the live site.
  Markup: .w-slider-mask > .w-slide, [data-slider-prev], [data-slider-next], optional .w-slider-nav.
  Each slide is translated by the active slide's offset (same technique as Webflow), so
  sliders with a visible mask (Latest work) show the following slides.
  Emits `mf-slider:change` with { index, total } (used by the homepage 1/9 counter).
*/
if (!customElements.get('mf-slider')) {
  class MfSlider extends HTMLElement {
    connectedCallback() {
      this.mask = this.querySelector('.w-slider-mask');
      this.slides = Array.from(this.mask ? this.mask.children : []);
      this.index = 0;
      this.infinite = this.dataset.infinite !== 'false';
      if (this.slides.length < 2) return;

      [
        ['[data-slider-prev]', -1],
        ['[data-slider-next]', 1],
      ].forEach(([selector, step]) => {
        const arrow = this.querySelector(selector);
        if (!arrow) return;
        arrow.addEventListener('click', () => this.go(this.index + step));
        // Arrows can be divs with role="button" (Webflow markup), so handle the keyboard too
        arrow.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.go(this.index + step);
        });
      });

      this.nav = this.querySelector('.w-slider-nav');
      if (this.nav && !this.nav.classList.contains('hide')) this.buildDots();

      this.bindSwipe();
      this.onResize = () => this.go(this.index, false);
      window.addEventListener('resize', this.onResize);
      this.go(0, false);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.onResize);
    }

    buildDots() {
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'w-slider-dot';
        dot.setAttribute('aria-label', `Show slide ${i + 1} of ${this.slides.length}`);
        dot.addEventListener('click', () => this.go(i));
        this.nav.appendChild(dot);
        return dot;
      });
    }

    bindSwipe() {
      // Bound to the whole slider: the invisible arrow halves sit on top of the slides.
      let startX = null;
      let swiped = false;
      this.addEventListener('pointerdown', (event) => {
        startX = event.clientX;
        swiped = false;
      });
      this.addEventListener('pointerup', (event) => {
        if (startX === null) return;
        const diff = startX - event.clientX;
        startX = null;
        if (Math.abs(diff) > 50) {
          swiped = true;
          this.go(this.index + (diff > 0 ? 1 : -1));
        }
      });
      this.addEventListener('pointercancel', () => {
        startX = null;
      });
      // A drag must not also fire the arrow / card link underneath.
      this.addEventListener(
        'click',
        (event) => {
          if (!swiped) return;
          swiped = false;
          event.preventDefault();
          event.stopPropagation();
        },
        true
      );
      this.addEventListener('dragstart', (event) => event.preventDefault());
    }

    go(target, animate = true) {
      const total = this.slides.length;
      let index = target;
      if (this.infinite) index = (target + total) % total;
      else index = Math.max(0, Math.min(total - 1, target));
      this.index = index;

      if (this.dataset.animation === 'cross') {
        // Webflow "cross": outgoing fades out while incoming fades in on top (500ms, crossOver 0)
        this.slides.forEach((slide, i) => {
          slide.style.transition = animate ? '' : 'none';
          slide.classList.toggle('is-active', i === index);
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
      } else {
        const offset = this.slides[index].offsetLeft - this.slides[0].offsetLeft;
        this.slides.forEach((slide, i) => {
          slide.style.transition = animate ? '' : 'none';
          slide.style.transform = `translateX(${-offset}px)`;
          slide.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
      }

      this.dots?.forEach((dot, i) => dot.classList.toggle('w-active', i === index));
      this.dispatchEvent(new CustomEvent('mf-slider:change', { bubbles: true, detail: { index, total } }));
    }
  }

  customElements.define('mf-slider', MfSlider);
}
